/**
 * useAccessGroupPrompt
 *
 * "Who should be able to ask about this file?": the upload access-group
 * question, shared by the chatbox (agent.vue) and the db file manager
 * (dbfile.vue).
 *
 * The project carries a single setting, `upload_access_group`, held in a record
 * of its own database (see @/code/access_group). When it names a group, callers
 * never reach this module: they apply it straight from
 * projectUploadAccessGroup(). When it is 'ask', the caller awaits one of the two
 * questions below, both of which surface the same modal offering Public /
 * Signed in users / Only me:
 *
 *   - promptAccessGroup(filename, fallback) asks about ONE file, and is what the
 *     chatbox uses: a chat attachment is decided on as it is sent, and there is
 *     no earlier moment at which the question could be asked.
 *
 *   - promptAccessGroupForBatch(count, fallback) asks ONCE about a whole upload,
 *     BEFORE anything is queued, and is what the db file manager uses. It exists
 *     because the per-file question was being asked per file AFTER that file had
 *     already uploaded: dropping thirty files uploaded all thirty and then
 *     interrogated the user about them one at a time, after the fact. It is the
 *     only one of the two that can be CANCELLED, because it is the only one
 *     asked while cancelling still means something (nothing has been queued
 *     yet); see cancelAccessGroup.
 *
 * BOTH QUESTIONS LIVE IN ONE MODULE, sharing one modal, one resolver and one
 * chain, rather than the batch one being a sibling module, because:
 *
 *   - There is one remembered answer per surface, and ONE reset. Whatever a
 *     surface last answered ("apply to all remaining files" per file, or the
 *     batch answer) has to be cleared when the next batch starts, by
 *     resetAccessGroupBatch(). Split across two modules that is two resets at
 *     four call sites in dbfile.vue, and the failure mode of forgetting one is
 *     silent: the next batch inherits an answer the user never gave it.
 *   - There is one modal element per surface, hence one resolver slot. Two
 *     modules would each own a `resolver`, and two questions that overlapped
 *     would render on top of each other with the second one stranding the
 *     first's promise, which is the exact hang the chain below exists to stop.
 *
 * The modal opens on 'authorized', the same value an unset project uses.
 *
 * Everything structural here is deliberately the same as useOverwritePrompt, for
 * the same reasons it is that way there, and the two prompts have to be able to
 * run back to back on one file without deadlocking:
 *
 *   - MODULE-LEVEL AND KEYED, not per component instance. An upload outlives the
 *     page that started it (the queue keeps draining across route changes), so a
 *     component-local prompt left a file awaiting a modal on a destroyed
 *     component: the question was never rendered, the promise never resolved,
 *     and that upload hung forever holding its worker. The key names the surface
 *     AND the project ("db:<serviceId>", "chat:<serviceId>") so two projects
 *     uploading at once never share one modal or one sticky answer. It is also
 *     what keeps the batch question off the chatbox: agent.vue holds the
 *     "chat:" instance, which nothing ever asks a batch question of, so its
 *     modal is only ever the per-file one it has always been.
 *
 *   - SERIALIZED. dbfile.vue runs several upload workers; without a chain they
 *     would race on the single modal and the later one would overwrite the
 *     earlier one's resolver, stranding it.
 *
 *   - "Apply to all remaining" makes the answer sticky for the rest of the batch.
 *     Call resetAccessGroupBatch() when a new batch starts, or the previous
 *     batch's answer silently applies to the next one.
 *
 * The chain is INDEPENDENT of useOverwritePrompt's: a file that hits both
 * questions asks them one after the other, and a shared chain would let one
 * prompt's pending promise block the other's.
 */
import { reactive } from "vue";
import {
    DEFAULT_UPLOAD_ACCESS_GROUP,
    normalizeUploadAccessGroup,
    type UploadAccessGroup,
} from "@/code/access_group";

export type AccessGroupPrompt = ReturnType<typeof createAccessGroupPrompt>;

const prompts = new Map<string, AccessGroupPrompt>();

export function useAccessGroupPrompt(key: string) {
    let existing = prompts.get(key);
    if (!existing) {
        existing = createAccessGroupPrompt();
        prompts.set(key, existing);
    }
    return existing;
}

function createAccessGroupPrompt() {
    const accessModal = reactive({
        open: false,
        filename: "",
        applyToAll: false,
        // Which question is on screen. The two read completely differently (one
        // names a file and offers "apply to all remaining files", the other
        // counts files and offers a cancel), so the template branches on this
        // rather than on `filename` being empty, which a file could never be but
        // a future caller could.
        batch: false,
        // How many files the batch question covers. Shown, so it has to be the
        // number of files that will actually be queued, not of the drop's
        // top-level entries.
        count: 0,
        // The currently highlighted option. Seeded from the project default each
        // time the modal opens, so the common answer is one click away.
        choice: DEFAULT_UPLOAD_ACCESS_GROUP as UploadAccessGroup,
    });

    // Nullable because a CANCELLED batch question resolves null. The per-file
    // question never sees one (see promptAccessGroup).
    let resolver: ((c: UploadAccessGroup | null) => void) | null = null;
    let stickyChoice: UploadAccessGroup | null = null;
    // The answer given for the current batch, up front, before anything was
    // queued. Read back by batchAccessGroup() at index time, which is minutes
    // after the question was answered and (routinely) after the page that asked
    // it was unmounted, so it lives here rather than in the component.
    let batchChoice: UploadAccessGroup | null = null;
    let chain: Promise<unknown> = Promise.resolve();

    function resetAccessGroupBatch() {
        stickyChoice = null;
        // Cleared with the sticky answer, at the same call sites, for the same
        // reason: a batch answer left standing is one the NEXT batch would apply
        // without ever having asked its own question.
        batchChoice = null;
        accessModal.applyToAll = false;
    }

    /**
     * What the current batch answered up front, or null when it has not been
     * asked (a project that names a group, a resumed batch, or a repair pass
     * running after a reload).
     *
     * A function rather than a value handed back at ask time: every reader of it
     * runs long after the question was answered, and a snapshot taken when the
     * upload started is exactly the value that goes stale.
     */
    function batchAccessGroup(): UploadAccessGroup | null {
        return batchChoice;
    }

    function promptAccessGroup(
        filename: string,
        fallback: UploadAccessGroup = DEFAULT_UPLOAD_ACCESS_GROUP,
    ): Promise<UploadAccessGroup> {
        if (stickyChoice) return Promise.resolve(stickyChoice);
        const run = chain.then(() => {
            // A file ahead of this one in the queue may have answered "apply to
            // all" while this one waited. Honour it without asking again.
            if (stickyChoice) return stickyChoice;
            accessModal.filename = filename;
            accessModal.applyToAll = false;
            accessModal.batch = false;
            accessModal.count = 0;
            accessModal.choice = normalizeUploadAccessGroup(fallback);
            accessModal.open = true;
            return new Promise<UploadAccessGroup>((resolve) => {
                // Coalesced because the resolver signature is shared with the
                // batch question, which may resolve null. Null can never reach
                // here (cancelAccessGroup refuses unless the modal is in batch
                // mode, and this one is not), and if it ever did, the caller is
                // an upload waiting to write an access group onto a record:
                // handing it null writes a record at no group at all, so it gets
                // the same value the modal opened on instead.
                resolver = (c) => resolve(c || normalizeUploadAccessGroup(fallback));
            });
        });
        chain = run.catch(() => undefined);
        return run;
    }

    /**
     * Ask ONCE for a whole batch, before a byte moves.
     *
     * Resolves the chosen group, or NULL when the question was dismissed, which
     * the caller must read as "upload nothing": this question is asked before
     * anything is queued precisely so that declining it can still cost nothing.
     *
     * Deliberately does NOT short-circuit on a remembered answer the way
     * promptAccessGroup does on stickyChoice. A new batch is a new question, and
     * every caller resets first anyway; short-circuiting here would mean a
     * second drop silently inheriting the first drop's answer, which is the bug
     * resetAccessGroupBatch exists to prevent.
     */
    function promptAccessGroupForBatch(
        count: number,
        fallback: UploadAccessGroup = DEFAULT_UPLOAD_ACCESS_GROUP,
    ): Promise<UploadAccessGroup | null> {
        const run = chain.then(() => {
            accessModal.filename = "";
            // No "apply to all remaining files" on this path: the whole point of
            // asking once, up front, is that there is nothing remaining to apply
            // it to. Cleared anyway so a box ticked on the per-file question of
            // an earlier batch cannot be read back by anything.
            accessModal.applyToAll = false;
            accessModal.batch = true;
            accessModal.count = count;
            accessModal.choice = normalizeUploadAccessGroup(fallback);
            accessModal.open = true;
            return new Promise<UploadAccessGroup | null>((resolve) => {
                resolver = resolve;
            });
        });
        chain = run.catch(() => undefined);
        return run;
    }

    /** Wired to the modal's confirm button. */
    function chooseAccessGroup(choice: UploadAccessGroup) {
        const picked = normalizeUploadAccessGroup(choice);
        if (accessModal.batch) {
            // The batch answer governs every file of this upload that has no
            // group of its own already, and lasts exactly as long as the batch:
            // resetAccessGroupBatch() clears it when the next one starts.
            batchChoice = picked;
        } else if (accessModal.applyToAll) {
            // "Apply to all remaining files" carries this answer through the REST
            // OF THIS BATCH and no further, cleared by the same reset, so a choice
            // made for one drag-and-drop never silently governs the next one.
            stickyChoice = picked;
        }
        accessModal.open = false;
        // `batch` is deliberately NOT cleared here. The modal element stays
        // mounted after `open` flips (it is a <dialog> with slotted content), so
        // flipping the mode on close repaints the closing modal with the OTHER
        // question's copy, about a filename that is now "". Every open sets the
        // mode, which is the only time it is read.
        const r = resolver;
        resolver = null;
        r?.(picked);
    }

    /**
     * Wired to the batch modal's Cancel, and to its dismiss.
     *
     * Only a BATCH question can be cancelled. The per-file one is asked from
     * inside an upload worker about a file whose bytes are already stored, so
     * there is no "never mind" to express: something has to be written as that
     * record's access group, and the modal correspondingly offers no way out.
     * Guarded rather than trusted to the template, because there are two
     * templates (dbfile.vue and agent.vue) and only one of them knows that.
     */
    function cancelAccessGroup() {
        if (!accessModal.batch) return;
        accessModal.open = false;
        const r = resolver;
        resolver = null;
        r?.(null);
    }

    /**
     * Settle whatever question is open, for a surface that is going away.
     *
     * Nothing else does this, and without it an unmount while the modal is open
     * strands the resolver: the promise never settles, so the upload that was waiting
     * on it is silently discarded (no files queued, no failure recorded, nothing on
     * screen), and `chain` is left pending FOR EVER on a module-level instance keyed
     * by project. Every later question for that project then chains off a dead promise
     * and never opens, so the next drop hangs behind a scrim that never lifts. One
     * route change during one question disables uploads for that project for the rest
     * of the session.
     *
     * Answers with the value each question type can accept: the batch question takes a
     * cancel (null, which aborts the batch and uploads nothing), and the per-file one
     * cannot, so it gets the value the modal was already showing, which is what
     * clicking Upload without touching the radios would have produced.
     */
    function releaseAccessGroupPrompt() {
        if (!resolver) return;
        const r = resolver;
        const wasBatch = accessModal.batch;
        const shown = accessModal.choice;
        resolver = null;
        accessModal.open = false;
        r(wasBatch ? null : shown);
    }

    return {
        accessModal,
        resetAccessGroupBatch,
        batchAccessGroup,
        promptAccessGroup,
        promptAccessGroupForBatch,
        chooseAccessGroup,
        cancelAccessGroup,
        releaseAccessGroupPrompt,
    };
}
